import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Badge, Button, Field, Select, TextInput, formatCurrency } from "@odingard/design-system";
import { ErrorState, LoadingState, EmptyState } from "@odingard/design-system";
import type { CatalogApplication, CatalogBundle } from "@odingard/sdk-client";
import { useAsync } from "../../lib/useAsync";
import { api } from "../../lib/api";
import { routeMeta, usePageMeta } from "../../lib/usePageMeta";

type SortKey = "sort" | "name";

// One shelf vocabulary for every storefront surface, so the root and the
// marketplace read as one catalog instead of two differently-worded pages.
// Applications shelve by the job they do; bundles are the one shelf that is not
// a category, because a bundle is a way to buy rather than a thing to do.
export const SHELVES = {
  apps: "Business Applications",
  bundles: "Suites & Bundles",
} as const;

// Availability is a badge on the card, not a shelf — but within a category the
// customer should still meet what they can buy today first.
export function byAvailability(a: CatalogApplication, b: CatalogApplication): number {
  const rank = (x: CatalogApplication) => (x.lifecycleStatus === "available" ? 0 : 1);
  return rank(a) - rank(b);
}

function monthlyPrice(app: CatalogApplication): string | null {
  const p = app.prices.month;
  return p ? `${formatCurrency(p.unitAmount, p.currency)}/mo` : null;
}

export function AppCard({ app }: { app: CatalogApplication }) {
  const comingSoon = app.lifecycleStatus === "coming_soon";
  const price = monthlyPrice(app);
  return (
    <Link
      to={`/marketplace/apps/${app.slug}`}
      className={`feature${comingSoon ? " feature--soon" : ""}`}
      style={{ textDecoration: "none", color: "inherit" }}
    >
      <div className="ods-row" style={{ justifyContent: "space-between" }}>
        <div className="feature__icon" aria-hidden="true">
          {app.icon}
        </div>
        {comingSoon ? <Badge tone="neutral">Coming soon</Badge> : <Badge tone="success">Available today</Badge>}
      </div>
      <h3>{app.name}</h3>
      <p>{app.tagline || app.description}</p>
      <div className="ods-row" style={{ justifyContent: "space-between", marginTop: "var(--ods-space-4)" }}>
        {app.category ? <Badge tone="neutral">{app.category.name}</Badge> : <span />}
        {!comingSoon && price && (
          <span style={{ color: "var(--ods-color-muted)", fontSize: "var(--ods-text-sm)" }}>from {price}</span>
        )}
      </div>
    </Link>
  );
}

function BundleCard({ b }: { b: CatalogBundle }) {
  const comingSoon = b.lifecycleStatus === "coming_soon";
  const icon = b.kind === "addon" ? "🛡️" : "📦";
  // A bundle's roadmap members are named as roadmap, so buying it never reads
  // as buying an application that cannot be provisioned yet.
  const shipped = b.applications.filter((a) => a.lifecycleStatus === "available");
  const roadmap = b.applications.filter((a) => a.lifecycleStatus !== "available");
  return (
    <Link to={`/marketplace/bundles/${b.slug}`} className="feature" style={{ textDecoration: "none", color: "inherit" }}>
      <div className="ods-row" style={{ justifyContent: "space-between" }}>
        <div className="feature__icon" aria-hidden="true">
          {icon}
        </div>
        {comingSoon ? <Badge tone="neutral">Coming soon</Badge> : <Badge tone="success">Available</Badge>}
      </div>
      <h3>{b.name}</h3>
      <p>{b.description}</p>
      {shipped.length > 0 && (
        <p style={{ color: "var(--ods-color-muted)", fontSize: "var(--ods-text-sm)", marginTop: "var(--ods-space-3)" }}>
          Available today: {shipped.map((a) => a.name).join(", ")}
          {roadmap.length > 0 && <> · Added as they ship: {roadmap.map((a) => a.name).join(", ")}</>}
        </p>
      )}
    </Link>
  );
}

export function Marketplace() {
  usePageMeta(routeMeta("/marketplace"));
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState("");
  // Category is in the URL so storefront category links and shared filters work.
  const category = params.get("category") ?? "";
  const setCategory = (next: string) => {
    const p = new URLSearchParams(params);
    if (next) p.set("category", next);
    else p.delete("category");
    setParams(p, { replace: true });
  };
  const [sort, setSort] = useState<SortKey>("sort");
  const [availableOnly, setAvailableOnly] = useState(false);

  const meta = useAsync(async () => {
    const [categories, bundles] = await Promise.all([api.catalogCategories(), api.catalogBundles()]);
    return { categories: categories.categories, bundles: bundles.bundles };
  }, []);

  const list = useAsync(
    async () => {
      const res = await api.catalogApplications({
        ...(q ? { q } : {}),
        ...(category ? { category } : {}),
        sort,
        pageSize: 100,
      });
      return res;
    },
    [q, category, sort],
  );

  const all = list.data?.applications ?? [];
  // Studio sells what a Studio account provisions. Anything with its own
  // workspace and subscription is somebody else's storefront.
  const platformApps = all.filter((a) => a.delivery === "platform");
  const shown = availableOnly
    ? platformApps.filter((a) => a.lifecycleStatus === "available")
    : platformApps;
  const bundles = meta.data?.bundles ?? [];
  const filters = useMemo(() => meta.data?.categories ?? [], [meta.data]);

  const showBundles = !q && !category && !availableOnly;
  const groups = useMemo(() => {
    const byCat = filters
      .map((c) => ({ cat: c, items: shown.filter((a) => a.category?.key === c.key).sort(byAvailability) }))
      .filter((g) => g.items.length > 0);
    const uncategorized = shown.filter((a) => !a.category).sort(byAvailability);
    return { byCat, uncategorized };
  }, [filters, shown]);

  return (
    <section className="section">
      <div className="container">
        <div className="section__head">
          <h1>Marketplace: buy governed AI applications by the firm</h1>
          <p>
            Every Odingard application, product and service in one place. Provision a Studio app instantly with a free
            trial, or explore what&apos;s coming next.
          </p>
        </div>

        <div
          className="ods-row"
          aria-label="Filter by category"
          style={{ gap: "var(--ods-space-2)", marginBottom: "var(--ods-space-5)", flexWrap: "wrap" }}
        >
          <Button size="sm" variant={category ? "ghost" : "primary"} onClick={() => setCategory("")}>
            All applications
          </Button>
          {filters.map((c) => (
            <Button
              key={c.key}
              size="sm"
              variant={category === c.key ? "primary" : "ghost"}
              onClick={() => setCategory(c.key)}
            >
              {c.name}
            </Button>
          ))}
          <Button
            size="sm"
            variant={availableOnly ? "primary" : "ghost"}
            aria-pressed={availableOnly}
            onClick={() => setAvailableOnly((v) => !v)}
            style={{ marginLeft: "auto" }}
          >
            Available today
          </Button>
        </div>

        <div
          className="ods-row"
          style={{ gap: "var(--ods-space-3)", marginBottom: "var(--ods-space-6)", flexWrap: "wrap", alignItems: "flex-end" }}
        >
          <div style={{ flex: "1 1 240px" }}>
            <Field label="Search">
              {(id) => (
                <TextInput
                  id={id}
                  placeholder="Search applications…"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                />
              )}
            </Field>
          </div>
          <div style={{ flex: "0 1 160px" }}>
            <Field label="Sort">
              {(id) => (
                <Select id={id} value={sort} onChange={(e) => setSort(e.target.value === "name" ? "name" : "sort")}>
                  <option value="sort">Featured</option>
                  <option value="name">Name (A to Z)</option>
                </Select>
              )}
            </Field>
          </div>
          <Link to="/marketplace/updates" style={{ marginLeft: "auto" }}>
            <Button variant="secondary" size="sm">
              What&apos;s new
            </Button>
          </Link>
        </div>

        {list.loading && <LoadingState label="Loading applications…" />}
        {list.error && <ErrorState description={list.error.message} onRetry={list.reload} />}
        {list.data && (
          <>
            {all.length === 0 ? (
              <EmptyState title="No applications match your filters" description="Try clearing search or category filters." />
            ) : (
              <>
                {groups.byCat.map(({ cat, items }) => (
                  <div className="mkt-shelf" key={cat.key}>
                    <div className="mkt-shelf__head">
                      <h3>{cat.name}</h3>
                      {cat.description && <p>{cat.description}</p>}
                      <span className="mkt-shelf__count">
                        {items.length} {items.length === 1 ? "app" : "apps"}
                      </span>
                    </div>
                    <div className="feature-grid">
                      {items.map((a) => (
                        <AppCard app={a} key={a.id} />
                      ))}
                    </div>
                  </div>
                ))}

                {groups.uncategorized.length > 0 && (
                  <div className="mkt-shelf">
                    <div className="mkt-shelf__head">
                      <h3>Applications</h3>
                    </div>
                    <div className="feature-grid">
                      {groups.uncategorized.map((a) => (
                        <AppCard app={a} key={a.id} />
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}

            {showBundles && bundles.length > 0 && (
              <div className="mkt-shelf">
                <div className="mkt-shelf__head">
                  <h3>{SHELVES.bundles}</h3>
                  <p>Buy the whole platform in one bill, or add depth.</p>
                  <span className="mkt-shelf__count">
                    {bundles.length} {bundles.length === 1 ? "bundle" : "bundles"}
                  </span>
                </div>
                <div className="feature-grid">
                  {bundles.map((b) => (
                    <BundleCard b={b} key={b.id} />
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
}
